import {getHostQueryClient} from "@agenta/shared/api"
import {projectIdAtom, sessionAtom} from "@agenta/shared/state"
import type {LlmProvider} from "@agenta/shared/types"
import {atom} from "jotai"
import {atomFamily} from "jotai-family"
import {atomWithQuery} from "jotai-tanstack-query"

import {fetchVaultSecret} from "../../secret/api"
import {
    buildAgentModelCandidates,
    selectableAgentHarnesses,
    subscriptionPairsFrom,
    toProviderConnections,
    type AgentModelCandidate,
    type BuiltinModelEndpoint,
    type ProviderConnection,
} from "../../secret/core"
import {subscriptionPairModelsAtom, vaultSecretsQueryAtom} from "../../secret/state"
import {
    fetchBuiltinModelEndpoints,
    fetchHarnessCapabilities,
    fetchSubscriptionStatus,
    type SubscriptionStatusResponse,
} from "../api"

import {harnessCatalogQueryAtom, type HarnessCapabilitiesMap} from "./inspectMeta"
import {
    SUBSCRIPTION_STATUS_QUERY_HARNESS,
    subscriptionStatusQueryAtomFamily,
} from "./subscriptionStatus"

/** Whether a candidate source answered, rejected, or never settled. */
export type AgentModelSourceOutcome = "ok" | "error" | "unsettled"

export interface AgentModelSourceReport {
    provider_connections: AgentModelSourceOutcome
    harness_catalog: AgentModelSourceOutcome
    subscription_status: AgentModelSourceOutcome
    /** HTTP status of the first errored source in the order above; 0 when the error carries none. */
    status: number
}

export interface AgentModelCandidatesState {
    status: "loading" | "error" | "ready"
    candidates: AgentModelCandidate[]
    connections: ProviderConnection[]
    capabilities: HarnessCapabilitiesMap | null
    error: unknown | null
    /** Set by `loadAgentModelCandidates` only; the atom family derives from live query state. */
    sources?: AgentModelSourceReport
}

interface CandidateSourceState {
    vaultRows?: LlmProvider[]
    vaultError?: unknown
    capabilities?: HarnessCapabilitiesMap
    capabilitiesError?: unknown
    subscriptionStatus?: SubscriptionStatusResponse | null
    subscriptionSettled: boolean
    /** The subscription check could not be MADE (rejected request), as opposed to answering "none". */
    subscriptionError?: unknown
    pairModelSelection?: Record<string, string[] | undefined> | null
    showSubscriptions: boolean
    builtinEndpoints?: BuiltinModelEndpoint[]
    /** False while the built-in endpoints read is in flight; credits may be the only route. */
    builtinSettled?: boolean
}

export const resolveAgentModelCandidateSources = ({
    vaultRows,
    vaultError,
    capabilities,
    capabilitiesError,
    subscriptionStatus,
    subscriptionSettled,
    subscriptionError,
    pairModelSelection,
    showSubscriptions,
    builtinEndpoints,
    builtinSettled = true,
}: CandidateSourceState): AgentModelCandidatesState => {
    const connections = toProviderConnections(vaultRows ?? [])
    if (!vaultRows || !capabilities) {
        const error = vaultError ?? capabilitiesError ?? null
        return {
            status: error ? "error" : "loading",
            candidates: [],
            connections,
            capabilities: null,
            error,
        }
    }
    // A failed check never blocks on itself: fall through and answer from the vault alone.
    if (showSubscriptions && !subscriptionSettled && !subscriptionError) {
        return {status: "loading", candidates: [], connections, capabilities, error: null}
    }
    if (!builtinSettled) {
        return {status: "loading", candidates: [], connections, capabilities, error: null}
    }

    const subscriptionPairs = showSubscriptions
        ? (subscriptionPairsFrom(subscriptionStatus?.harnesses ?? {}) ?? [])
        : []
    const candidates = buildAgentModelCandidates({
        connections,
        capabilities,
        harnessIds: selectableAgentHarnesses(Object.keys(capabilities)),
        showSubscriptions,
        subscriptionPairs,
        pairModelSelection,
        builtinEndpoints,
    })

    // A check we could not MAKE is not a deployment with no subscription. Reading a rejected
    // request as "no pairs" turns an unreachable runner into "you have no runnable model", and the
    // gate that follows tells the user to add a provider key the agent may not even use.
    //
    // It only matters when nothing else is runnable: with vault candidates in hand the gate's
    // question is already answered yes, and the pairs we could not read would have added more, not
    // fewer. So report the failure only in the case where it changes the answer, and let the gate
    // stand down there rather than blocking the composer on a claim we never established.
    if (
        showSubscriptions &&
        subscriptionStatus === undefined &&
        subscriptionError &&
        candidates.length === 0
    ) {
        return {
            status: "error",
            candidates: [],
            connections,
            capabilities,
            error: subscriptionError,
        }
    }
    return {status: "ready", candidates, connections, capabilities, error: null}
}

const builtinModelEndpointsQueryKey = (projectId: string | null) => [
    "gateways",
    "llms",
    "builtin",
    projectId,
]

/**
 * Built-in model endpoints. A failed read leaves `data` undefined, which the candidate builder
 * treats as no built-in source, so the vault rows still show. Gated on the session so a
 * pre-auth request is not sent at all.
 */
export const builtinModelEndpointsQueryAtom = atomWithQuery<BuiltinModelEndpoint[]>((get) => {
    const projectId = get(projectIdAtom)
    return {
        queryKey: builtinModelEndpointsQueryKey(projectId),
        queryFn: () => fetchBuiltinModelEndpoints(projectId as string),
        enabled: get(sessionAtom) && Boolean(projectId),
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        // Optional rows: a refusal (403 where built-ins are off) is final, as in the loader below.
        retry: false,
    }
})

export const agentModelCandidatesAtomFamily = atomFamily((showSubscriptions: boolean) =>
    atom<AgentModelCandidatesState>((get) => {
        const vault = get(vaultSecretsQueryAtom)
        const harnessCatalog = get(harnessCatalogQueryAtom)
        const builtin = get(builtinModelEndpointsQueryAtom)
        const subscription = showSubscriptions
            ? get(subscriptionStatusQueryAtomFamily(SUBSCRIPTION_STATUS_QUERY_HARNESS))
            : null

        return resolveAgentModelCandidateSources({
            vaultRows: vault.data,
            vaultError: vault.data === undefined && vault.isError ? vault.error : undefined,
            capabilities: harnessCatalog.data,
            capabilitiesError:
                harnessCatalog.data === undefined && harnessCatalog.isError
                    ? harnessCatalog.error
                    : undefined,
            subscriptionStatus: subscription?.data,
            subscriptionSettled: !showSubscriptions || subscription?.data !== undefined,
            subscriptionError:
                subscription?.data === undefined && subscription?.isError
                    ? subscription.error
                    : undefined,
            pairModelSelection: showSubscriptions ? get(subscriptionPairModelsAtom) : null,
            showSubscriptions,
            builtinEndpoints: builtin.data,
            builtinSettled: !builtin.isLoading,
        })
    }),
)

const AGENT_CREATION_SOURCE_RETRY_DELAY_MS = 250

/** Retry one failed source read and log the underlying error for diagnosis. */
const retryAgentCreationSource =
    (source: "provider connections" | "harness catalog") =>
    (failureCount: number, error: unknown): boolean => {
        const willRetry = failureCount < 1
        if (willRetry) {
            console.error(`[agent-create] Retrying ${source} after failure`, {
                attempt: failureCount + 1,
                error,
            })
        }
        return willRetry
    }

const sourceOutcome = (result: {data: unknown; error: unknown}): AgentModelSourceOutcome => {
    if (result.error !== undefined) return "error"
    return result.data === undefined ? "unsettled" : "ok"
}

const httpStatusFrom = (error: unknown): number => {
    if (!error || typeof error !== "object") return 0
    const direct = (error as {status?: unknown}).status
    if (typeof direct === "number") return direct
    const response = (error as {response?: {status?: unknown}}).response
    return typeof response?.status === "number" ? response.status : 0
}

export async function loadAgentModelCandidates({
    projectId,
    userId,
    pairModelSelection,
    showSubscriptions = true,
    refreshVault = false,
}: {
    projectId: string
    userId: string
    pairModelSelection?: Record<string, string[] | undefined> | null
    showSubscriptions?: boolean
    refreshVault?: boolean
}): Promise<AgentModelCandidatesState> {
    const queryClient = getHostQueryClient()
    const vaultQuery = {
        queryKey: ["vault", "secrets", userId, projectId],
        queryFn: () => fetchVaultSecret({projectId}),
        staleTime: 5 * 60_000,
        retry: retryAgentCreationSource("provider connections"),
        retryDelay: AGENT_CREATION_SOURCE_RETRY_DELAY_MS,
    } as const
    const [vault, capabilities, subscription, builtinEndpoints] = await Promise.all([
        (refreshVault
            ? queryClient.fetchQuery<LlmProvider[]>({...vaultQuery, staleTime: 0})
            : queryClient.ensureQueryData<LlmProvider[]>(vaultQuery)
        )
            .then((data) => ({data, error: undefined}))
            .catch((error: unknown) => ({data: undefined, error})),
        queryClient
            .ensureQueryData<HarnessCapabilitiesMap>({
                queryKey: ["workflows", "catalog", "harnesses"],
                queryFn: async () =>
                    (await fetchHarnessCapabilities()) as unknown as HarnessCapabilitiesMap,
                staleTime: 5 * 60_000,
                retry: retryAgentCreationSource("harness catalog"),
                retryDelay: AGENT_CREATION_SOURCE_RETRY_DELAY_MS,
            })
            .then((data) => ({data, error: undefined}))
            .catch((error: unknown) => ({data: undefined, error})),
        showSubscriptions
            ? queryClient
                  .ensureQueryData<SubscriptionStatusResponse | null>({
                      queryKey: [
                          "workflows",
                          "runtime",
                          "subscription-status",
                          SUBSCRIPTION_STATUS_QUERY_HARNESS,
                          projectId,
                      ],
                      queryFn: () =>
                          fetchSubscriptionStatus({
                              harness: SUBSCRIPTION_STATUS_QUERY_HARNESS,
                              projectId,
                          }),
                      staleTime: 10_000,
                      retry: false,
                  })
                  .then((data) => ({data, error: undefined}))
                  .catch((error: unknown) => ({data: undefined, error}))
            : Promise.resolve({data: null, error: undefined}),
        queryClient
            .ensureQueryData<BuiltinModelEndpoint[]>({
                queryKey: builtinModelEndpointsQueryKey(projectId),
                queryFn: () => fetchBuiltinModelEndpoints(projectId),
                staleTime: 5 * 60_000,
                retry: false,
            })
            // Only adds picker rows, so a failure must not block the vault's.
            .catch(() => undefined),
    ])

    const resolved = resolveAgentModelCandidateSources({
        vaultRows: vault.data,
        vaultError: vault.error,
        capabilities: capabilities.data,
        capabilitiesError: capabilities.error,
        subscriptionStatus: subscription.data,
        subscriptionSettled: subscription.data !== undefined,
        subscriptionError: subscription.error,
        pairModelSelection,
        showSubscriptions,
        builtinEndpoints,
    })

    return {
        ...resolved,
        sources: {
            provider_connections: sourceOutcome(vault),
            harness_catalog: sourceOutcome(capabilities),
            subscription_status: sourceOutcome(subscription),
            status: httpStatusFrom(vault.error ?? capabilities.error ?? subscription.error),
        },
    }
}
